import React from 'react';
import CodeBlock from '../CodeBlock';
import ConfigPanel from '../ConfigPanel';
import ErrorDocs from '../ErrorDocs';

const Overview = ({ onNavigateToWorkflow }) => {
  // Use environment variable if available, otherwise default to localhost:8000
  const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

  const handleWorkflowClick = (workflowId) => {
    if (onNavigateToWorkflow) {
      onNavigateToWorkflow(workflowId);
    }
  };

  return (
    <section className="section active">
      <ConfigPanel />
      <div className="mb-8">
        <h1 className="text-4xl md:text-5xl font-black tracking-tight mb-4 text-white">Drawbridge API</h1>
        <p className="text-lg md:text-xl text-gray-300 leading-7 max-w-[65ch]">
          A comprehensive REST API for managing documents, generating Standard Operating Procedures (SOPs),
          orchestrating multi-agent workflows, and analyzing knowledge through corpus maps.
        </p>
      </div>

      {/* Base URL and Quick Info */}
      <div className="card mb-6 bg-gray-900 border-gray-800">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <div className="mb-2">
              <h3 className="text-lg font-semibold tracking-tight text-gray-200">Base URL</h3>
            </div>
            <code className="text-blue-400 text-sm font-mono">{BASE_URL}</code>
            <p className="text-sm text-gray-400 mt-2">All API requests should be made to this base URL</p>
          </div>
          <div>
            <div className="mb-2">
              <h3 className="text-lg font-semibold tracking-tight text-gray-200">OpenAPI Spec</h3>
            </div>
            <a
              href={`${BASE_URL}/openapi.json`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-green-400 hover:text-green-300 text-sm"
            >
              View OpenAPI Schema
            </a>
            <p className="text-sm text-gray-400 mt-2">Machine-readable API specification</p>
          </div>
        </div>
      </div>

      {/* Quick Start */}
      <div className="card mb-6">
        <h2 className="text-2xl font-bold tracking-tight text-gray-200 mb-3">Quick Start</h2>
        <p className="text-gray-300 mb-5 leading-6 max-w-[65ch]">
          Get up and running with Drawbridge API in just a few steps. Create your account, organize your documents, and start generating SOPs.
        </p>
        <div className="bg-gray-900 rounded-lg p-5 mb-6 border border-gray-800">
          <ol className="space-y-4 text-sm text-gray-300 list-none ml-0">
            <li className="flex gap-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-500 text-white flex items-center justify-center font-bold text-xs mt-0.5">1</span>
              <span className="flex-1">
                <strong className="text-white font-semibold">Create your account</strong> — Register to receive your API key for authentication
              </span>
            </li>
            <li className="flex gap-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-500 text-white flex items-center justify-center font-bold text-xs mt-0.5">2</span>
              <span className="flex-1">
                <strong className="text-white font-semibold">Create a namespace</strong> — Organize your documents and enable ingestion workflows
              </span>
            </li>
            <li className="flex gap-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-500 text-white flex items-center justify-center font-bold text-xs mt-0.5">3</span>
              <span className="flex-1">
                <strong className="text-white font-semibold">Upload documents and generate SOPs</strong> — Process your content and create structured operating procedures
              </span>
            </li>
          </ol>
        </div>
        <div className="flex gap-3">
          <button
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded font-medium transition-colors text-sm"
            onClick={() => handleWorkflowClick('getting-started')}
          >
            View Getting Started Guide →
          </button>
        </div>
      </div>

      {/* Authentication */}
      <div className="card mb-6">
        <div className="mb-4">
          <h2 className="text-2xl font-bold tracking-tight text-gray-200">Authentication</h2>
        </div>
        <p className="text-gray-300 mb-4 leading-7 max-w-[65ch]">
          All endpoints (except user creation) require authentication via API Key. The API key format is <code className="bg-gray-900 px-1.5 py-0.5 rounded">email:api_key</code>.
        </p>

        <div className="space-y-4">
          <div>
            <h3 className="text-lg font-semibold tracking-tight mb-2 text-gray-200">Header (Recommended)</h3>
            <p className="text-sm text-gray-400 mb-2 leading-6">Include your API key in the X-API-Key header:</p>
            <CodeBlock code="X-API-Key: email@example.com:your_api_key_here" />
          </div>

          <div>
            <h3 className="text-lg font-semibold tracking-tight mb-2 text-gray-200">Query Parameter</h3>
            <p className="text-sm text-gray-400 mb-2 leading-6">Alternatively, you can pass the API key as a query parameter:</p>
            <CodeBlock code="?apiKey=email@example.com:your_api_key_here" />
          </div>

          <div className="mt-4 p-4 bg-yellow-900/20 border border-yellow-700/50 rounded">
            <p className="text-sm text-yellow-200">
              <strong>⚠️ Security Note:</strong> API keys should be stored securely and never exposed in client-side code or version control.
            </p>
          </div>
        </div>
      </div>

      {/* Error Schema / Common Errors */}
      <ErrorDocs />

      {/* Secondary Workflow - Document Management */}
      <div className="card mb-6">
        <h2 className="text-2xl font-bold tracking-tight mb-4 text-gray-200">Manage Documents</h2>
        <p className="text-gray-300 mb-4 leading-6 max-w-[65ch]">
          Upload, process, and manage your documents. Essential for ongoing operations and keeping your knowledge base up to date.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <button
            onClick={() => handleWorkflowClick('document-ingestion')}
            className="p-4 bg-gray-900 rounded-lg border border-gray-800 hover:border-green-500 transition-colors cursor-pointer text-left w-full"
          >
            <h3 className="font-semibold mb-2 text-green-400">Document Ingestion</h3>
            <p className="text-sm text-gray-400">Upload and process documents</p>
          </button>
          <button
            onClick={() => handleWorkflowClick('corpus-map')}
            className="p-4 bg-gray-900 rounded-lg border border-gray-800 hover:border-blue-500 transition-colors cursor-pointer text-left w-full"
          >
            <h3 className="font-semibold mb-2 text-blue-400">Structure Exploration</h3>
            <p className="text-sm text-gray-400">Generate corpus maps to explore your dataset structure</p>
          </button>
        </div>
      </div>

    </section>
  );
};

export default Overview;
